<script setup lang="ts">
import tryArrayIndex from "@/lib/tryArrayIndex";
import type { Coin } from '@/lib/interfaces/calculator';
import DeepdiveLink from "../../utils/DeepdiveLink.vue";
import {
  Listbox,
  ListboxButton,
  ListboxOptions,
} from "@headlessui/vue";
import Input from "@/components/utils/Input.vue";
import { defineAsyncComponent, ref } from "vue";
import { useFuse } from "@vueuse/integrations/useFuse.mjs";
import Content from "./Content.vue";
import getDeepdiveUrl from "@lib/getDeepdiveUrl.ts";

interface Props {
  availableCoins: Coin[]
}

const props = defineProps<Props>()

const selectedCoins = defineModel<string[]>('selectedCoins', {
  required: true,
})

function selectedCoinsLabel(coins: Coin[]) {

  const selectedCoinItems: Coin[] = coins.filter(coin => selectedCoins.value.includes(coin.value))

  if (selectedCoinItems.length <= 0) return "No coins selected";
  else if (selectedCoinItems.length === 1) return tryArrayIndex(selectedCoinItems, 0).value;
  else if (selectedCoinItems.length === 2) return `${tryArrayIndex(selectedCoinItems, 0).value}, ${tryArrayIndex(selectedCoinItems, 1).value}`;
  else if (selectedCoinItems.length === 3) return `${tryArrayIndex(selectedCoinItems, 0).value}, ${tryArrayIndex(selectedCoinItems, 1).value} and 1 other coin`;
  else return `${tryArrayIndex(selectedCoinItems, 0).value}, ${tryArrayIndex(selectedCoinItems, 1).value} and ${selectedCoinItems.length - 2} other coins`;
}

const search = ref("");

const options = {
  fuseOptions: {
    keys: ["value"],
  },
  matchAllWhenSearchEmpty: true,
};

const { results: fuseAvailableCoins } = useFuse(search, props.availableCoins, options);

const AsyncContent = defineAsyncComponent(async () => Content)

</script>

<template>
  <div class="flex justify-between items-center">
    <span class="font-semibold text-brand-secondary"> {{$t('selected_coins')}} </span>
    <DeepdiveLink label="Why?" :href="getDeepdiveUrl('details/calculator/autopilot')" />
  </div>
  <div class="relative">
    <Listbox>
      <ListboxButton
        :class="[
          'w-full px-4 py-2.5 justify-between flex items-center border-2 border-brand-stroke/10 rounded-full mt-2.5',
        ]"
      >
        <div class="font-semibold text-sm text-brand-secondary">
          {{ selectedCoinsLabel(availableCoins) }}
        </div>
        <span class="i-ri:edit-line bg-brand-gradient text-lg inline-block" />
      </ListboxButton>
      <Transition
        enter-active-class="duration-300 ease-out"
        enter-from-class="opacity-0 -translate-y-2"
      >
        <ListboxOptions
          :class="[
            'absolute top-0 border-2 border-brand-stroke/10 rounded-3 px-4 pt-4 w-full bg-brand-background z-1000',
          ]"
        >
          <Input
            v-model="search"
            :placeholder="$t('search_for_crypto')"
            icon="i-octicon:search-16"
            class="w-full"
          />
          <div class="mt-4 h-48" >
            <Suspense>
              <AsyncContent
                v-model:selectedCoins="selectedCoins"
                :availableCoins="fuseAvailableCoins"
              />
              <template #fallback>
                {{$t('selected_coins')}}
                {{ $t('loading_coins') }}...
              </template>
            </Suspense>          
          </div>
        </ListboxOptions>        
      </Transition>
    </Listbox>
  </div>
</template>
